import { confirmToast } from "../../utils/toast";
import React, { useEffect, useMemo, useState } from "react";
import axios from "../../utils/axiosConfig";
import {
  collectValidationErrors,
  showValidationAlert,
  validateEmail,
  validateMobile,
  validatePAN,
  validatePhone,
  validateRequired,
  validateWebsite
} from "../../utils/fieldValidation";

const BuyerMaster = () => {
  const initialForm = {
    BuyerName: "",
    FullName: "",
    IEC_Code: "",
    GSTNo: "",
    PANNo: "",
    OpeningBalanceAsOn20260401: "",
    Address: "",
    Area: "",
    City: "",
    State: "",
    Country: "",
    Zip: "",
    ISD_Code: "",
    Phone: "",
    Fax_ISD_Code: "",
    Fax: "",
    Mobile: "",
    Email: "",
    WebSite: "",
    ShippingInstructions: "",
    Remark: "",
    ContactPerson: "",
    UserId: "",
    Password: "",
    InTime: "",
    OutTime: "",
    IsLoginReq: false,
    BankStatus: "",
    ModifyBy: ""
  };

  const initialContact = {
    ContactPerson: "",
    Designation: "",
    Department: "",
    ISD_Code: "",
    ContactNo: "",
    ExtNo: "",
    MobileNo: "",
    Email: ""
  };

  const [form, setForm] = useState(initialForm);
  const [contactForm, setContactForm] = useState(initialContact);
  const [editingContact, setEditingContact] = useState(null);
  const [savingContact, setSavingContact] = useState(false);
  const [buyers, setBuyers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [groupIds, setGroupIds] = useState([]);
  const [items, setItems] = useState([]);
  const [itemIds, setItemIds] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [activeTab, setActiveTab] = useState("list");
  const [showBuyerDetails, setShowBuyerDetails] = useState(true);
  const [showContactDetails, setShowContactDetails] = useState(true);
  const [showInterestedItems, setShowInterestedItems] = useState(true);
  const [showItemModal, setShowItemModal] = useState(false);
  const [modalItemIds, setModalItemIds] = useState([]);
  const [selectedInterestedItemIds, setSelectedInterestedItemIds] = useState([]);
  const [itemModalPage, setItemModalPage] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 5;
  const itemModalRowsPerPage = 8;
  const isNumericId = (value) => value !== "" && value !== null && value !== undefined && !Number.isNaN(Number(value));
  const normalizeCompanyKey = (value) => String(value || "").trim().toLowerCase();

  useEffect(() => {
    fetchBuyers();
    fetchGroups();
    fetchItems();
    fetchCountries();
  }, []);

  const fetchBuyers = async () => {
    const res = await axios.get("/buyers");
    setBuyers(Array.isArray(res.data) ? res.data : []);
  };

  const fetchGroups = async () => {
    const res = await axios.get("/contact-groups");
    setGroups(Array.isArray(res.data) ? res.data : []);
  };

  const fetchItems = async () => {
    const res = await axios.get("/buyers/items");
    setItems(Array.isArray(res.data) ? res.data : []);
  };

  const fetchCountries = async () => {
    try {
      const res = await axios.get("/countries");
      setCountries(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      alert("Unable to load countries. Please reload the page and try again.");
    }
  };

  const fetchStates = async (countryId) => {
    if (!isNumericId(countryId)) {
      setStates([]);
      return [];
    }

    try {
      const res = await axios.get(`/states/by-country/${countryId}`);
      const rows = Array.isArray(res.data) ? res.data : [];
      setStates(rows);
      return rows;
    } catch (err) {
      setStates([]);
      alert("Unable to load states. Please select the country again.");
      return [];
    }
  };

  const fetchContacts = async (buyerId) => {
    try {
      const res = await axios.get(`/buyers/${buyerId}/contacts`);
      setContacts(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setContacts([]);
    }
  };

  const fetchBuyerGroups = async (buyerId) => {
    try {
      const res = await axios.get(`/buyers/${buyerId}/groups`);
      setGroupIds(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setGroupIds([]);
    }
  };

  const fetchBuyerItems = async (buyerId) => {
    try {
      const res = await axios.get(`/buyers/${buyerId}/items`);
      setItemIds(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setItemIds([]);
    }
  };

  const handleChange = async (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = type === "checkbox"
      ? checked
      : (name === "GSTNo" || name === "PANNo" ? value.toUpperCase() : value);

    setForm((prev) => ({ ...prev, [name]: nextValue }));

    if (name === "Country") {
      const country = countries.find((c) => String(c.CountryId) === String(value));
      setForm((prev) => ({
        ...prev,
        Country: value,
        State: "",
        City: "",
        ISD_Code: prev.ISD_Code || country?.ISD_Code || "",
        Fax_ISD_Code: prev.Fax_ISD_Code || country?.ISD_Code || ""
      }));
      await fetchStates(value);
    }

    if (name === "State") {
      setForm((prev) => ({ ...prev, State: value, City: "" }));
    }
  };

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setContactForm((prev) => ({ ...prev, [name]: value }));
  };

  const toggleGroup = (id) => {
    setGroupIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const openItemModal = () => {
    setModalItemIds(itemIds);
    setItemModalPage(1);
    setShowItemModal(true);
  };

  const toggleModalItem = (id) => {
    setModalItemIds((prev) =>
      prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]
    );
  };

  const applyModalItems = () => {
    setItemIds(modalItemIds);
    setSelectedInterestedItemIds((prev) => prev.filter((id) => modalItemIds.includes(id)));
    setShowItemModal(false);
  };

  const toggleInterestedItemSelection = (id) => {
    setSelectedInterestedItemIds((prev) =>
      prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]
    );
  };

  const removeSelectedInterestedItems = () => {
    setItemIds((prev) => prev.filter((id) => !selectedInterestedItemIds.includes(id)));
    setSelectedInterestedItemIds([]);
  };

  const resetForm = () => {
    setForm(initialForm);
    setContactForm(initialContact);
    setEditingContact(null);
    setContacts([]);
    setGroupIds([]);
    setItemIds([]);
    setModalItemIds([]);
    setSelectedInterestedItemIds([]);
    setShowItemModal(false);
    setItemModalPage(1);
    setStates([]);
    setEditingId(null);
    setShowBuyerDetails(true);
    setShowContactDetails(true);
    setShowInterestedItems(true);
  };

  const validateContactRows = (rows) => {
    return rows.flatMap((contact, index) => {
      const label = contact.ContactPerson?.trim() || `Contact ${index + 1}`;
      return collectValidationErrors([
        validatePhone(contact.ContactNo, `${label} contact no`),
        validateMobile(contact.MobileNo, `${label} mobile no`),
        validateEmail(contact.Email, `${label} email`)
      ]);
    });
  };

  const validateBuyerForm = () => {
    const buyerNameKey = normalizeCompanyKey(form.BuyerName);
    const fullNameKey = normalizeCompanyKey(form.FullName);
    const duplicateBuyer = buyers.find((buyer) => {
      if (editingId && String(buyer.BuyerID) === String(editingId)) return false;

      return (
        (buyerNameKey && normalizeCompanyKey(buyer.BuyerName) === buyerNameKey) ||
        (fullNameKey && normalizeCompanyKey(buyer.FullName) === fullNameKey)
      );
    });

    const errors = collectValidationErrors([
      validateRequired(form.BuyerName, "Buyer company name"),
      validateRequired(form.GSTNo, "GST No"),
      validateRequired(form.PANNo, "PAN No"),
      validatePAN(form.PANNo, "PAN No"),
      validatePhone(form.Phone, "Buyer phone"),
      validatePhone(form.Fax, "Buyer fax"),
      validateMobile(form.Mobile, "Buyer mobile"),
      validateEmail(form.Email, "Buyer email"),
      validateWebsite(form.WebSite, "Buyer website"),
      duplicateBuyer ? `Buyer company already exists: ${duplicateBuyer.BuyerName || duplicateBuyer.FullName}` : null,
      ...validateContactRows(contacts)
    ]);

    return !showValidationAlert(errors);
  };

  const validateContactForm = () => {
    const errors = collectValidationErrors([
      validatePhone(contactForm.ContactNo, "Contact no"),
      validateMobile(contactForm.MobileNo, "Mobile no"),
      validateEmail(contactForm.Email, "Contact email")
    ]);

    return !showValidationAlert(errors);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateBuyerForm()) return;

    try {
      let buyerId = editingId;

      if (editingId) {
        await axios.put(`/buyers/${editingId}`, form);
      } else {
        const res = await axios.post("/buyers", form);
        buyerId = res.data.BuyerID;
      }

      await axios.post(`/buyers/${buyerId}/groups`, { groupIds });
      await axios.post(`/buyers/${buyerId}/items`, { itemIds });

      const pendingContacts = contacts.filter((c) => !c.ContactId);
      for (const contact of pendingContacts) {
        await axios.post(`/buyers/${buyerId}/contacts`, contact);
      }

      alert(editingId ? "Buyer updated successfully" : "Buyer saved successfully");
      await fetchBuyers();
      resetForm();
      setActiveTab("list");
    } catch (err) {
      console.error("Buyer save failed:", err);
      const message = err.response?.data?.message || err.response?.data || err.message;
      alert(`Error saving buyer: ${message}`);
    }
  };

  const editBuyer = async (buyer) => {
    setEditingContact(null);
    setContactForm(initialContact);
    setShowBuyerDetails(true);
    setShowContactDetails(true);
    setShowInterestedItems(true);
    setForm({
      BuyerName: buyer.BuyerName || "",
      FullName: buyer.FullName || "",
      IEC_Code: buyer.IEC_Code || "",
      GSTNo: buyer.GSTNo || "",
      PANNo: buyer.PANNo || "",
      OpeningBalanceAsOn20260401: buyer.OpeningBalanceAsOn20260401 ?? "",
      Address: buyer.Address || "",
      Area: buyer.Area || "",
      City: buyer.CityName || buyer.City || "",
      State: buyer.State || "",
      Country: buyer.Country || "",
      Zip: buyer.Zip || "",
      ISD_Code: buyer.ISD_Code || "",
      Phone: buyer.Phone || "",
      Fax_ISD_Code: buyer.Fax_ISD_Code || "",
      Fax: buyer.Fax || "",
      Mobile: buyer.Mobile || "",
      Email: buyer.Email || "",
      WebSite: buyer.WebSite || "",
      ShippingInstructions: buyer.ShippingInstructions || "",
      Remark: buyer.Remark || "",
      ContactPerson: buyer.ContactPerson || "",
      UserId: buyer.UserId || "",
      Password: "",
      InTime: buyer.InTime || "",
      OutTime: buyer.OutTime || "",
      IsLoginReq: !!buyer.IsLoginReq,
      BankStatus: buyer.BankStatus || "",
      ModifyBy: buyer.ModifyBy || ""
    });

    setEditingId(buyer.BuyerID);
    setActiveTab("form");
    await fetchStates(buyer.Country);
    await fetchContacts(buyer.BuyerID);
    await fetchBuyerGroups(buyer.BuyerID);
    await fetchBuyerItems(buyer.BuyerID);
  };

  const handleDelete = async (id) => {
    if (!await confirmToast("Delete this buyer?")) return;
    await axios.delete(`/buyers/${id}`);
    await fetchBuyers();
    if (editingId === id) resetForm();
  };

  const addContact = async () => {
    if (!contactForm.ContactPerson.trim()) {
      alert("Contact person is required");
      return;
    }

    if (!validateContactForm()) return;

    setSavingContact(true);
    try {
      if (editingContact?.ContactId) {
        await axios.put(`/buyers/${editingId}/contacts/${editingContact.ContactId}`, contactForm);
        await fetchContacts(editingId);
      } else if (editingContact) {
        setContacts(prev => prev.map(contact => contact.LocalId === editingContact.LocalId
          ? { ...contact, ...contactForm } : contact));
      } else if (editingId) {
        await axios.post(`/buyers/${editingId}/contacts`, contactForm);
        await fetchContacts(editingId);
      } else {
        setContacts(prev => [{ ...contactForm, LocalId: `${Date.now()}-${prev.length}` }, ...prev]);
      }
      setContactForm(initialContact);
      setEditingContact(null);
    } catch (err) {
      alert(`Unable to save contact: ${err.response?.data?.message || err.response?.data || err.message}`);
    } finally {
      setSavingContact(false);
    }
  };

  const editContact = (contact) => {
    setEditingContact(contact);
    setContactForm(Object.fromEntries(Object.keys(initialContact).map(key => [key, contact[key] ?? ""])));
  };

  const deleteContact = async (contact) => {
    if (!await confirmToast("Delete this contact?")) return;
    if (editingContact && (contact.ContactId ? contact.ContactId === editingContact.ContactId : contact.LocalId === editingContact.LocalId)) {
      setEditingContact(null);
      setContactForm(initialContact);
    }

    if (contact.ContactId) {
      await axios.delete(`/buyers/contacts/${contact.ContactId}`);
      await fetchContacts(editingId);
    } else {
      setContacts((prev) => prev.filter((c) => c.LocalId !== contact.LocalId));
    }
  };

  const countryNameById = useMemo(() => {
    return countries.reduce((acc, country) => {
      acc[String(country.CountryId)] = country.CountryName;
      return acc;
    }, {});
  }, [countries]);

  const indexOfLast = currentPage * rowsPerPage;
  const indexOfFirst = indexOfLast - rowsPerPage;
  const currentBuyers = buyers.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(buyers.length / rowsPerPage);
  const interestedItems = useMemo(
    () => items.filter((item) => itemIds.includes(item.ItemID)),
    [items, itemIds]
  );
  const itemModalTotalPages = Math.max(1, Math.ceil(items.length / itemModalRowsPerPage));
  const modalItems = items.slice(
    (itemModalPage - 1) * itemModalRowsPerPage,
    itemModalPage * itemModalRowsPerPage
  );
  const allInterestedItemsSelected = interestedItems.length > 0 && interestedItems.every(
    (item) => selectedInterestedItemIds.includes(item.ItemID)
  );
  const allModalPageItemsSelected = modalItems.length > 0 && modalItems.every(
    (item) => modalItemIds.includes(item.ItemID)
  );

  const toggleAllInterestedItems = () => {
    setSelectedInterestedItemIds(
      allInterestedItemsSelected ? [] : interestedItems.map((item) => item.ItemID)
    );
  };

  const toggleAllModalPageItems = () => {
    const pageIds = modalItems.map((item) => item.ItemID);
    setModalItemIds((prev) => allModalPageItemsSelected
      ? prev.filter((id) => !pageIds.includes(id))
      : [...new Set([...prev, ...pageIds])]
    );
  };

  return (
    <div className="container-fluid buyer-master-page">
      <h4 className="fw-bold mb-3">Buyer Master</h4>

      <div className="card shadow-sm border-0">
        <div className="card-header bg-white">
          <ul className="nav nav-tabs card-header-tabs">
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${activeTab === "list" ? "active" : ""}`}
                onClick={() => setActiveTab("list")}
              >
                Buyer List
              </button>
            </li>
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${activeTab === "form" ? "active" : ""}`}
                onClick={() => setActiveTab("form")}
              >
                {editingId ? "Edit Buyer" : "Add Buyer"}
              </button>
            </li>
          </ul>
        </div>

        <div className="card-body">
          {activeTab === "list" && (
            <>
              <div className="d-flex justify-content-between mb-3">
                <h5 className="mb-0">Buyer List</h5>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    resetForm();
                    setActiveTab("form");
                  }}
                >
                  Add Buyer
                </button>
              </div>

              <div className="table-responsive">
                <table className="table table-bordered table-hover align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Short Company Name</th>
                      <th>Full Company Name</th>
                      <th>GST No</th>
                      <th>PAN No</th>
                      <th>Country</th>
                      <th>City</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentBuyers.map((buyer) => (
                      <tr key={buyer.BuyerID}>
                        <td>{buyer.BuyerName}</td>
                        <td>{buyer.FullName}</td>
                        <td>{buyer.GSTNo}</td>
                        <td>{buyer.PANNo}</td>
                        <td>{buyer.CountryName || countryNameById[String(buyer.Country)] || buyer.Country}</td>
                        <td>{buyer.CityName || buyer.City}</td>
                        <td>{buyer.Email}</td>
                        <td>{buyer.Phone}</td>
                        <td className="text-center">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary me-2"
                            onClick={() => editBuyer(buyer)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(buyer.BuyerID)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <span>
                    Showing {indexOfFirst + 1} to {Math.min(indexOfLast, buyers.length)} of {buyers.length}
                  </span>
                  <ul className="pagination mb-0">
                    {Array.from({ length: totalPages }, (_, index) => (
                      <li
                        key={index}
                        className={`page-item ${currentPage === index + 1 ? "active" : ""}`}
                      >
                        <button
                          type="button"
                          className="page-link"
                          onClick={() => setCurrentPage(index + 1)}
                        >
                          {index + 1}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}

          {activeTab === "form" && (
            <form onSubmit={handleSubmit} autoComplete="off" className="buyer-master-form">
              <div className="border rounded bg-white mb-3 buyer-master-section master-collapse-section">
                <div
                  className="px-3 py-2 border-bottom fw-bold text-muted d-flex justify-content-between align-items-center"
                  style={{ cursor: "pointer" }}
                  onClick={() => setShowBuyerDetails((show) => !show)}
                >
                  <span>Buyer Information</span>
                  <span>{showBuyerDetails ? "▲" : "▼"}</span>
                </div>

                {showBuyerDetails && (
                  <div className="p-3 buyer-details-content">
                    <div className="buyer-section-note">
                      Company, address and communication information
                    </div>
                    <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label fw-semibold">Short Company Name *</label>
                  <input
                    name="BuyerName"
                    className="form-control"
                    value={form.BuyerName}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label fw-semibold">Full Company Name</label>
                  <input
                    name="FullName"
                    className="form-control"
                    value={form.FullName}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label fw-semibold">IEC Code *</label>
                  <input
                    name="IEC_Code"
                    className="form-control"
                    value={form.IEC_Code}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label fw-semibold">GST No *</label>
                  <input
                    name="GSTNo"
                    className="form-control"
                    value={form.GSTNo}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label fw-semibold">PAN No *</label>
                  <input
                    name="PANNo"
                    className="form-control"
                    value={form.PANNo}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label fw-semibold">Opening Balance as on 1st Apr 2026</label>
                  <input
                    type="number"
                    step="0.01"
                    name="OpeningBalanceAsOn20260401"
                    className="form-control"
                    value={form.OpeningBalanceAsOn20260401}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-md-12">
                  <label className="form-label fw-semibold">Address</label>
                  <textarea
                    name="Address"
                    className="form-control"
                    rows="3"
                    value={form.Address}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label fw-semibold">Country</label>
                  <select
                    name="Country"
                    className="form-select"
                    value={form.Country}
                    onChange={handleChange}
                  >
                    <option value="">Select Country</option>
                    {countries.map((country) => (
                      <option key={country.CountryId} value={country.CountryId}>
                        {country.CountryName}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-3">
                  <label className="form-label fw-semibold">State</label>
                  <select
                    name="State"
                    className="form-select"
                    value={form.State}
                    onChange={handleChange}
                  >
                    <option value="">Select State</option>
                    {states.map((state) => (
                      <option key={state.StateId} value={state.StateId}>
                        {state.StateName}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-3">
                  <label className="form-label fw-semibold">City</label>
                  <input
                    name="City"
                    className="form-control"
                    value={form.City}
                    onChange={handleChange}
                    placeholder="Enter city"
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label fw-semibold">Zip</label>
                  <input
                    name="Zip"
                    className="form-control"
                    value={form.Zip}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label fw-semibold">Phone</label>
                  <input
                    name="Phone"
                    className="form-control"
                    value={form.Phone}
                    onChange={handleChange}
                    placeholder="Enter phone number"
                    inputMode="tel"
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label fw-semibold">Fax</label>
                  <input
                    name="Fax"
                    className="form-control"
                    value={form.Fax}
                    onChange={handleChange}
                    placeholder="Enter fax number"
                    inputMode="tel"
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label fw-semibold">Mobile</label>
                  <input
                    name="Mobile"
                    className="form-control"
                    value={form.Mobile}
                    onChange={handleChange}
                    inputMode="tel"
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label fw-semibold">Email</label>
                  <input
                    name="Email"
                    type="email"
                    className="form-control"
                    value={form.Email}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">Web Site</label>
                  <input
                    name="WebSite"
                    className="form-control"
                    value={form.WebSite}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">Area</label>
                  <input
                    name="Area"
                    className="form-control"
                    value={form.Area}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">Shipping Instruction</label>
                  <textarea
                    name="ShippingInstructions"
                    className="form-control"
                    rows="3"
                    value={form.ShippingInstructions}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">Remarks</label>
                  <textarea
                    name="Remark"
                    className="form-control"
                    rows="3"
                    value={form.Remark}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-12">
                  <label className="form-label fw-semibold">Contact Group</label>
                  <div className="border rounded p-2" style={{ maxHeight: 150, overflowY: "auto" }}>
                    <div className="row g-2">
                      {groups.map((group) => (
                        <div key={group.GroupId} className="col-md-3 col-sm-6">
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={groupIds.includes(group.GroupId)}
                              onChange={() => toggleGroup(group.GroupId)}
                            />
                            <label className="form-check-label">{group.GroupDesc}</label>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                    </div>

                    {!editingId && (
                      <>
                        <hr />
                        <h5 className="mb-3">Login</h5>
                        <div className="row g-3">
                          <div className="col-md-3">
                            <label className="form-label fw-semibold">User ID</label>
                            <input
                              name="UserId"
                              className="form-control"
                              value={form.UserId}
                              onChange={handleChange}
                              autoComplete="off"
                            />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label fw-semibold">Password</label>
                            <input
                              type="password"
                              name="Password"
                              className="form-control"
                              value={form.Password}
                              onChange={handleChange}
                              autoComplete="new-password"
                            />
                          </div>
                          <div className="col-md-2">
                            <label className="form-label fw-semibold">In Time</label>
                            <input
                              name="InTime"
                              className="form-control"
                              value={form.InTime}
                              onChange={handleChange}
                            />
                          </div>
                          <div className="col-md-2">
                            <label className="form-label fw-semibold">Out Time</label>
                            <input
                              name="OutTime"
                              className="form-control"
                              value={form.OutTime}
                              onChange={handleChange}
                            />
                          </div>
                          <div className="col-md-2 d-flex align-items-end">
                            <div className="form-check">
                              <input
                                type="checkbox"
                                name="IsLoginReq"
                                className="form-check-input"
                                checked={form.IsLoginReq}
                                onChange={handleChange}
                              />
                              <label className="form-check-label fw-semibold">Login Available</label>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              <div className="border rounded bg-white mb-3 buyer-master-section master-collapse-section">
                <div
                  className="px-3 py-2 border-bottom fw-bold text-muted d-flex justify-content-between align-items-center"
                  style={{ cursor: "pointer" }}
                  onClick={() => setShowContactDetails((show) => !show)}
                >
                  <span>Contact Persons</span>
                  <span>{showContactDetails ? "▲" : "▼"}</span>
                </div>

                {showContactDetails && (
                  <div className="p-3 contact-details-content">
                    <div className="buyer-section-note">
                      Add the people responsible for this buyer account
                    </div>
                    <div className="row g-3 mb-3">
                <div className="col-md-4">
                  <label className="form-label fw-semibold">Contact Person</label>
                  <input
                    name="ContactPerson"
                    className="form-control"
                    placeholder="Contact Person"
                    value={contactForm.ContactPerson}
                    onChange={handleContactChange}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold">Designation</label>
                  <input
                    name="Designation"
                    className="form-control"
                    placeholder="Designation"
                    value={contactForm.Designation}
                    onChange={handleContactChange}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold">Department</label>
                  <input
                    name="Department"
                    className="form-control"
                    placeholder="Department"
                    value={contactForm.Department}
                    onChange={handleContactChange}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold">Contact No</label>
                  <input
                    name="ContactNo"
                    className="form-control"
                    placeholder="Contact No"
                    value={contactForm.ContactNo}
                    onChange={handleContactChange}
                    inputMode="tel"
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label fw-semibold">Extension</label>
                  <input
                    name="ExtNo"
                    className="form-control"
                    placeholder="Ext"
                    value={contactForm.ExtNo}
                    onChange={handleContactChange}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-semibold">Mobile No</label>
                  <input
                    name="MobileNo"
                    className="form-control"
                    placeholder="Mobile No"
                    value={contactForm.MobileNo}
                    onChange={handleContactChange}
                    inputMode="tel"
                  />
                </div>
                <div className="col-md-8 contact-email-field">
                  <label className="form-label fw-semibold">Email</label>
                  <input
                    type="email"
                    name="Email"
                    className="form-control"
                    placeholder="Email"
                    value={contactForm.Email}
                    onChange={handleContactChange}
                  />
                </div>
                <div className="col-md-3 contact-isd-field">
                  <label className="form-label fw-semibold">ISD Code</label>
                  <input
                    name="ISD_Code"
                    className="form-control"
                    placeholder="ISD Code"
                    value={contactForm.ISD_Code}
                    onChange={handleContactChange}
                  />
                </div>
                <div className="col-md-4 d-flex align-items-end contact-add-action">
                  <button
                    type="button"
                    className="btn btn-success w-100"
                    disabled={savingContact}
                    onClick={addContact}
                  >
                    {savingContact ? "Saving..." : editingContact ? "Update Detail" : "Add Detail"}
                  </button>
                  {editingContact && <button type="button" className="btn btn-secondary ms-2" disabled={savingContact}
                    onClick={() => { setEditingContact(null); setContactForm(initialContact); }}>Cancel</button>}
                </div>
                    </div>

                    <div className="table-responsive">
                <table className="table table-bordered align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Contact Person</th>
                      <th>Designation</th>
                      <th>Department</th>
                      <th>Contact No</th>
                      <th>Ext</th>
                      <th>Mobile No</th>
                      <th>Email</th>
                      <th className="text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contacts.map((contact) => (
                      <tr key={contact.ContactId || contact.LocalId}>
                        <td>{contact.ContactPerson}</td>
                        <td>{contact.Designation}</td>
                        <td>{contact.Department}</td>
                        <td>{contact.ContactNo}</td>
                        <td>{contact.ExtNo}</td>
                        <td>{contact.MobileNo}</td>
                        <td>{contact.Email}</td>
                        <td className="text-center">
                          <button type="button" className="btn btn-sm btn-outline-primary me-2"
                            disabled={savingContact} onClick={() => editContact(contact)}>Edit</button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => deleteContact(contact)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                    </div>
                  </div>
                )}
              </div>

              <div className="border rounded bg-white mb-3 buyer-master-section master-collapse-section">
                <div
                  className="px-3 py-2 border-bottom fw-bold text-muted d-flex justify-content-between align-items-center"
                  style={{ cursor: "pointer" }}
                  onClick={() => setShowInterestedItems((show) => !show)}
                >
                  <span>Interested Items</span>
                  <span>{showInterestedItems ? "▲" : "▼"}</span>
                </div>

                {showInterestedItems && (
                  <div className="p-3">
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                      <div className="buyer-section-note mb-0">
                        Items currently marked as interested for this buyer
                      </div>
                      <div>
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm me-2"
                          disabled={selectedInterestedItemIds.length === 0}
                          onClick={removeSelectedInterestedItems}
                        >
                          Remove Selected
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          onClick={openItemModal}
                        >
                          Add Items
                        </button>
                      </div>
                    </div>
                    <div className="table-responsive border rounded interested-items-panel">
                      <table className="table table-bordered table-hover align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th className="text-center" style={{ width: 56 }}>
                              <input
                                type="checkbox"
                                className="form-check-input"
                                aria-label="Select all interested items"
                                checked={allInterestedItemsSelected}
                                onChange={toggleAllInterestedItems}
                                disabled={interestedItems.length === 0}
                              />
                            </th>
                            <th>Item Name</th>
                          </tr>
                        </thead>
                        <tbody>
                          {interestedItems.length === 0 ? (
                            <tr>
                              <td colSpan="2" className="text-center text-muted py-3">
                                No interested items selected.
                              </td>
                            </tr>
                          ) : interestedItems.map((item) => (
                            <tr key={item.ItemID}>
                              <td className="text-center">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  aria-label={`Select ${item.ItemName || item.ItemCode}`}
                                  checked={selectedInterestedItemIds.includes(item.ItemID)}
                                  onChange={() => toggleInterestedItemSelection(item.ItemID)}
                                />
                              </td>
                              <td>{item.ItemName || item.ItemCode}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              <div className="buyer-master-actions">
                <button type="submit" className="btn btn-success me-2">
                  {editingId ? "Update" : "Save"}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    resetForm();
                    setActiveTab("list");
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {showItemModal && (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true" aria-labelledby="interestedItemsModalTitle">
            <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title" id="interestedItemsModalTitle">Select Interested Items</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setShowItemModal(false)}
                  />
                </div>
                <div className="modal-body">
                  <div className="table-responsive">
                    <table className="table table-bordered table-hover align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th className="text-center" style={{ width: 56 }}>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              aria-label="Select all items on this page"
                              checked={allModalPageItemsSelected}
                              onChange={toggleAllModalPageItems}
                              disabled={modalItems.length === 0}
                            />
                          </th>
                          <th style={{ width: "180px" }}>Item Code</th>
                          <th>Item Name</th>
                        </tr>
                      </thead>
                      <tbody>
                        {modalItems.length === 0 ? (
                          <tr>
                            <td colSpan="3" className="text-center text-muted py-4">No items found.</td>
                          </tr>
                        ) : modalItems.map((item) => (
                          <tr key={item.ItemID}>
                            <td className="text-center">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                aria-label={`Select ${item.ItemName || item.ItemCode}`}
                                checked={modalItemIds.includes(item.ItemID)}
                                onChange={() => toggleModalItem(item.ItemID)}
                              />
                            </td>
                            <td>{item.ItemCode}</td>
                            <td>{item.ItemName || item.ItemCode}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="modal-footer justify-content-between">
                  <div className="d-flex align-items-center gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      disabled={itemModalPage === 1}
                      onClick={() => setItemModalPage((page) => Math.max(1, page - 1))}
                    >
                      Previous
                    </button>
                    <span className="small text-muted">
                      Page {itemModalPage} of {itemModalTotalPages}
                    </span>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      disabled={itemModalPage === itemModalTotalPages}
                      onClick={() => setItemModalPage((page) => Math.min(itemModalTotalPages, page + 1))}
                    >
                      Next
                    </button>
                  </div>
                  <div>
                    <button type="button" className="btn btn-secondary me-2" onClick={() => setShowItemModal(false)}>
                      Cancel
                    </button>
                    <button type="button" className="btn btn-primary" onClick={applyModalItems}>
                      Add Selected ({modalItemIds.length})
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </div>
  );
};

export default BuyerMaster;
